import React, { useState } from 'react';
import apiService from '../../services/ApiService';
import { usePortfolioContext } from './PortfolioContext';
import { StaleBadge } from './StalenessIndicator';
import ModelScenarioPicker from './ModelScenarioPicker';
import StalenessReview from './StalenessReview';
import { useNumberLocale, formatFixed } from '../../utils/numberFormat';

export default function AssetManager() {
    const { selectedGroup, selectGroup, refreshGroups, canWrite } = usePortfolioContext();
    const numberLocale = useNumberLocale();
    const [showAddForm, setShowAddForm] = useState(false);
    const [showStalenessReview, setShowStalenessReview] = useState(false);
    const [historyAssetId, setHistoryAssetId] = useState<number | null>(null);

    if (!selectedGroup) return null;

    const handleRemoveAsset = async (assetId: number) => {
        await apiService.removePortfolioAsset(selectedGroup.id, assetId);
        // Refresh both the selected group (asset list) and the groups list (sidebar asset_count)
        await Promise.all([selectGroup(selectedGroup.id), refreshGroups()]);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900">Assets ({selectedGroup.assets.length})</h3>
                {canWrite && (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                        <span className="material-symbols-outlined text-[16px]">add</span>
                        Add Asset
                    </button>
                )}
            </div>

            {selectedGroup.assets.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg">
                    <span className="material-symbols-outlined text-[40px] text-slate-300 block mb-2">business</span>
                    <p className="text-sm text-slate-500">No assets yet. Add models to this portfolio group.</p>
                </div>
            ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Model</th>
                                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Scenario</th>
                                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Company</th>
                                <th className="text-right px-4 py-2.5 font-medium text-slate-600">Equity %</th>
                                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Tags</th>
                                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                                <th className="text-center px-4 py-2.5 font-medium text-slate-600">History</th>
                                <th className="px-4 py-2.5 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedGroup.assets.map((asset: any) => (
                                <tr key={asset.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                                    <td className="px-4 py-3 font-medium text-slate-900">{asset.model_name || `Model ${asset.model_id}`}</td>
                                    <td className="px-4 py-3 text-slate-600">{asset.scenario_name || `Scenario ${asset.scenario_id}`}</td>
                                    <td className="px-4 py-3 text-slate-600">{asset.company_name || `Company ${asset.company_model_data_id}`}</td>
                                    <td className="px-4 py-3 text-right text-slate-700">{formatFixed(asset.equity_percentage * 100, numberLocale, 1)}%</td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1 flex-wrap">
                                            {asset.tags?.map((tag: any) => (
                                                <span
                                                    key={tag.id}
                                                    className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                                                    style={tag.color ? { backgroundColor: tag.color + '20', color: tag.color } : {}}
                                                >
                                                    {tag.name}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <StaleBadge isStale={asset.is_stale} />
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <button
                                            onClick={() => { setHistoryAssetId(asset.id); setShowStalenessReview(true); }}
                                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors"
                                            title="View approval history"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">history</span>
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        {canWrite && (
                                            <button
                                                onClick={() => handleRemoveAsset(asset.id)}
                                                className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                                title="Remove asset"
                                            >
                                                <span className="material-symbols-outlined text-[18px]">close</span>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showStalenessReview && (
                <StalenessReview
                    initialAssetId={historyAssetId}
                    onClose={() => { setShowStalenessReview(false); setHistoryAssetId(null); selectGroup(selectedGroup.id); }}
                />
            )}

            {showAddForm && (
                <AddAssetForm
                    groupId={selectedGroup.id}
                    existingCompanyIds={selectedGroup.assets.map((a: any) => a.company_model_data_id)}
                    onClose={() => setShowAddForm(false)}
                    onAdded={() => { setShowAddForm(false); selectGroup(selectedGroup.id); refreshGroups(); }}
                />
            )}
        </div>
    );
}

function AddAssetForm({ groupId, existingCompanyIds, onClose, onAdded }: { groupId: number; existingCompanyIds: number[]; onClose: () => void; onAdded: () => void }) {
    const [pickerResult, setPickerResult] = useState<any>(null);
    const [equityPct, setEquityPct] = useState('100');
    const [commitmentOverride, setCommitmentOverride] = useState('');
    const [saving, setSaving] = useState(false);

    const handlePickerSelect = (result: any) => {
        setPickerResult(result);
    };

    const handleSubmit = async () => {
        if (!pickerResult) return;

        setSaving(true);
        try {
            await apiService.addPortfolioAsset(groupId, {
                model_id: pickerResult.model_id,
                scenario_id: pickerResult.scenario_id,
                company_model_data_id: pickerResult.company_model_data_id,
                equity_percentage: parseFloat(equityPct) / 100,
                commitment_override: commitmentOverride ? parseFloat(commitmentOverride) : null,
                tag_ids: [],
            });
            onAdded();
        } finally {
            setSaving(false);
        }
    };

    // Show picker if no selection yet
    if (!pickerResult) {
        return <ModelScenarioPicker onSelect={handlePickerSelect} onCancel={onClose} disabledCompanyIds={existingCompanyIds} />;
    }

    // Show equity % and commitment form after picker selection
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Configure Asset</h3>

                    {/* Selected asset summary */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-4 space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Model</span>
                            <span className="font-medium text-slate-900">{pickerResult.model_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Scenario</span>
                            <span className="font-medium text-slate-900">{pickerResult.scenario_name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Company</span>
                            <span className="font-medium text-slate-900">{pickerResult.company_name}</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Equity Ownership (%)</label>
                            <input
                                type="number"
                                value={equityPct}
                                onChange={(e) => setEquityPct(e.target.value)}
                                step="0.1"
                                min="0"
                                max="100"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Commitment Override
                                <span className="text-slate-400 font-normal ml-1">(optional, leave empty to infer from model)</span>
                            </label>
                            <input
                                type="number"
                                value={commitmentOverride}
                                onChange={(e) => setCommitmentOverride(e.target.value)}
                                step="1"
                                min="0"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Auto-inferred from model"
                            />
                        </div>

                        <div className="flex justify-between pt-2">
                            <button
                                onClick={() => setPickerResult(null)}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Change selection
                            </button>
                            <div className="flex gap-3">
                                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {saving ? 'Adding...' : 'Add Asset'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
